import { defineStore } from 'pinia';

import { getMaklerInfo, getBaseData } from "@/api/common/commonApi";
import type { MaklerOrAgencyIdentifier } from "@/api/zugang/types";
import { call } from "@/api/lib/integration";
import { computed, ref } from "vue";
import SHA256 from 'crypto-js/sha256';
import type { MaklervollmachtStatus } from "@/api/maklerVollmacht/types";
import { useAppUser } from "@/application/composables/useAppUser.ts";

export type BaseDataStoreState = {
  isCompany: boolean | null,
  isAuthenticated: boolean | null,
  firmenname: string | null,
  maklerOrAgencyUid: string | null,
  makleremail: string | null,
  maklertelefon: string | null,
  maklertelefonMobil: string | null,
  maklername: string | null,
  maklerIsAgency: boolean | null,
  isBroker: boolean | null,
  isMagicLogin: boolean | null,
  kundeDuzen: boolean,
  kundeVorname?: string | null,
  kundeNachname?: string | null,
  kundeEmail: string | null,
  twoFactorEnabled: boolean,
  hasPasswortSet: boolean,
  isFirstLogin: boolean,
  user: User | null,
  maklervollmachtStatus: MaklervollmachtStatus | null,
  isBanksApiUser: boolean | null,
};

export type User = {
  id: number,
  client_id: number,
  isShallow: boolean
}

export const useBaseDataStore = defineStore('baseDataStore', () => {
  const isCompany =  ref<null | boolean>(null);
  const isAuthenticated = ref<null | boolean>(null);
  const firmenname = ref<null | string>(null);
  const maklerOrAgencyUid = ref<null | string>(null);
  const makleremail = ref<null | string>(null);
  const maklertelefon = ref<null | string>(null);
  const maklertelefonMobil = ref<null | string>(null);
  const maklername = ref<null | string>(null);
  const maklerIsAgency = ref<null | boolean>(null);
  const isBroker = ref<null | boolean>(null);
  const isMagicLogin = ref<null | boolean>(null);
  const kundeDuzen = ref<boolean>(false);
  const kundeVorname = ref<null | string>(null);
  const kundeNachname = ref<null | string>(null);
  const kundeEmail = ref<null | string>(null);
  const twoFactorEnabled = ref<boolean>(false);
  const hasPasswortSet = ref<boolean>(false);
  const isFirstLogin = ref<boolean>(false);
  const user = ref<null | User >(null);
  const maklervollmachtStatus = ref<MaklervollmachtStatus | null>(null);
  const isBanksApiUser = ref<null | boolean>(null);

  const hashedUserId = computed(() => user.value ? SHA256(`${user.value.id}`).toString() : '');

  function $reset(): void {
    isCompany.value = null;
    isAuthenticated.value = null;
    firmenname.value = null;
    maklerOrAgencyUid.value = null;
    makleremail.value = null;
    maklertelefon.value = null;
    maklertelefonMobil.value = null;
    maklername.value = null;
    maklerIsAgency.value = null;
    isBroker.value = null;
    isMagicLogin.value = null;
    kundeDuzen.value = false;
    kundeVorname.value = null;
    kundeNachname.value = null;
    kundeEmail.value = null;
    twoFactorEnabled.value = false;
    hasPasswortSet.value = false;
    isFirstLogin.value = false;
    user.value = null;
    maklervollmachtStatus.value = null;
    isBanksApiUser.value = false;
  }

  async function fetchBaseData() {
    await call(
      getBaseData(),
      (data) => {
          isCompany.value = data.isCompany;
          isAuthenticated.value = data.isAuthenticated;
          firmenname.value = data.firmenname;
          makleremail.value = data.makleremail;
          maklertelefon.value = data.maklertelefon;
          maklertelefonMobil.value = data.maklertelefonMobil;
          maklername.value = data.maklername;
          maklerIsAgency.value = data.maklerIsAgency;
          isBroker.value = data.isBroker;
          isMagicLogin.value = data.isMagicLogin;
          kundeDuzen.value = data.kundeDuzen;
          kundeVorname.value = data.kundeVorname ?? null;
          kundeNachname.value = data.kundeNachname ?? null;
          kundeEmail.value = data.kundeEmail;
          twoFactorEnabled.value = data.twoFactorEnabled;
          hasPasswortSet.value = data.hasPasswortSet;
          isFirstLogin.value = data.isFirstLogin;
          user.value = data.user;
          maklervollmachtStatus.value = data.maklervollmachtStatus;
          isBanksApiUser.value = data.isBanksApiUser;

          useAppUser().set(data.user)
      },
    );
  }

  async function fetchMaklerInfos (maklerOrAgency: MaklerOrAgencyIdentifier, uid: string) {
    maklerOrAgencyUid.value = uid;
    maklerIsAgency.value = maklerOrAgency === 'f';

    await call(
      getMaklerInfo({ maklerOrAgency, uid }),
      (data) => {
          maklername.value = data.maklername;
          firmenname.value = data.firmenname;
          makleremail.value = data.makleremail;
          maklertelefon.value = data.maklertelefon;
          maklertelefonMobil.value = data.maklertelefonMobil;
          maklerIsAgency.value = data.maklerIsAgency;
      },
    );
  }

  function duzen(duText: string, sieText: string): string {
    return kundeDuzen.value ? duText : sieText;
  }

  return {
    isCompany,
    isAuthenticated,
    firmenname,
    maklerOrAgencyUid,
    makleremail,
    maklertelefon,
    maklertelefonMobil,
    maklername,
    maklerIsAgency,
    isBroker,
    isMagicLogin,
    kundeDuzen,
    kundeVorname,
    kundeNachname,
    kundeEmail,
    twoFactorEnabled,
    hasPasswortSet,
    isFirstLogin,
    user,
    hashedUserId,
    maklervollmachtStatus,
    isBanksApiUser,
    $reset,
    fetchBaseData,
    fetchMaklerInfos,
    duzen,
  };
});
